// Konfigurátor „Nastav/Uprav skupiny“ — výběr typů po úrovních + bucketování (sloučeniny) + parametry pro dynamické def_type
// Viz doc/plans/station-group-layout.md sekce I.1 body 1–6
//
// Dynamické úrovně (elevation_interval/elevation_cluster/spatial_cluster) nemají checkbox seznam bucketů —
// jen typ zdroje a parametr. Skutečné clustery se počítají zvlášť pro každého rodiče až při stavbě stromu
// (computeGroupTree.js), protože např. "10 stanic na cluster" musí dát jiné clustery pro každý kraj zvlášť.
import { useState, useEffect, useRef } from 'react'
import { useStationGroupTypes, useStationGroupSources, useStationGroupsBySource } from '../../hooks/useStationGroups'
import { ModalOverlay } from '../ModalOverlay'

const DYNAMIC_DEF_TYPES = new Set(['elevation_interval', 'elevation_cluster', 'spatial_cluster', 'spatial_count'])

// Tri-stavový checkbox „Přidat vše / Odebrat vše“ — ✓ všechny zatrženy, prázdný žádná, − částečně
function TriStateCheckbox({ state, onClick, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center text-[10px] leading-none font-bold ${
        state === 'all'
          ? 'bg-blue-500 border-blue-500 text-white'
          : state === 'some'
            ? 'bg-blue-100 border-blue-400 text-blue-600'
            : 'bg-white border-gray-300 text-transparent'
      }`}
    >
      {state === 'all' ? '✓' : state === 'some' ? '−' : '·'}
    </button>
  )
}

// Světové strany pro metodu dělení "podle počtu" — jazyk zkratek volí uživatel
const SPLIT_LANG_LABELS = {
  cs: 'české (S/J/V/Z)',
  en: 'anglické (N/S/E/W)',
}
const SPLIT_PARTS_OPTIONS = [
  { value: 'auto', label: 'auto (dle počtu stanic)' },
  { value: '2', label: '2 díly (osa)' },
  { value: '4', label: '4 díly (kvadranty)' },
  { value: '5', label: '5 dílů (kvadranty + střed)' },
  { value: '9', label: '9 dílů (8 směrů + střed)' },
]
const SPLIT_METHOD_OPTIONS = [
  { value: 'count', label: 'podle počtu (geometricky)' },
  { value: 'spatial_cluster', label: 'prostorový cluster' },
  { value: 'elevation_cluster', label: 'výškový cluster' },
  { value: 'elevation_interval', label: 'výškový interval' },
]

// Editor jednoho override — slučování a dělení podskupin platné jen pro jednu nadřízenou větev
function OverrideEditor({ override, subgroups, onChange, onRemove }) {
  const [mergeSel, setMergeSel] = useState([])
  const [splitCode, setSplitCode] = useState('')
  const [splitMethod, setSplitMethod] = useState('count')
  const [splitParts, setSplitParts] = useState('auto')
  const [splitPerGroup, setSplitPerGroup] = useState(10)
  const [splitLang, setSplitLang] = useState('cs')
  const [splitParam, setSplitParam] = useState(10)

  const mergedCodes = new Set(override.merges.flatMap(m => m.memberCodes))
  const splitCodes = new Set(override.splits.map(s => s.memberCode))
  const usedCodes = new Set([...mergedCodes, ...splitCodes])
  const freeSubgroups = subgroups.filter(sg => !usedCodes.has(sg.code))

  const doMerge = () => {
    if (mergeSel.length < 2) return
    const name = window.prompt('Název sloučeniny (v této větvi):')
    if (!name) return
    if (override.merges.some(m => m.label === name) || subgroups.some(sg => sg.label === name && !mergeSel.includes(sg.code))) {
      window.alert('Název koliduje s jiným kbelíkem v této větvi.')
      return
    }
    onChange({ ...override, merges: [...override.merges, { label: name, memberCodes: [...mergeSel] }] })
    setMergeSel([])
  }

  const doSplit = () => {
    if (!splitCode) return
    const spec = { memberCode: splitCode, method: splitMethod }
    if (splitMethod === 'count') {
      spec.parts = splitParts
      spec.lang = splitLang
      if (splitParts === 'auto') spec.perGroup = Number(splitPerGroup) || 10
    } else {
      spec.param = Number(splitParam) || 0
    }
    onChange({ ...override, splits: [...override.splits, spec] })
    setSplitCode('')
  }

  return (
    <div className="border border-gray-200 rounded-lg p-2 flex flex-col gap-2 bg-gray-50">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-gray-700">▸ {override.parentPath.join(' › ')}</span>
        <button className="text-red-400 underline" onClick={onRemove}>Odebrat</button>
      </div>

      {/* Sloučení */}
      <div className="flex flex-col gap-0.5">
        <div className="text-[11px] font-semibold text-gray-500">Sloučit (jen v této větvi)</div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 max-h-32 overflow-y-auto">
          {freeSubgroups.map(sg => (
            <label key={sg.code} className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={mergeSel.includes(sg.code)}
                onChange={() => setMergeSel(s => s.includes(sg.code) ? s.filter(c => c !== sg.code) : [...s, sg.code])}
                className="accent-blue-500"
              />
              {sg.label}
            </label>
          ))}
        </div>
        {mergeSel.length >= 2 && (
          <button className="text-xs text-blue-600 underline text-left" onClick={doMerge}>Sloučit vybrané ({mergeSel.length})</button>
        )}
        {override.merges.map(m => (
          <div key={m.label} className="flex items-center gap-1.5 text-xs bg-blue-50 rounded px-1 py-0.5">
            <span className="flex-1"><span className="font-medium">{m.label}</span> = {m.memberCodes.map(c => subgroups.find(s => s.code === c)?.label ?? c).join(' + ')}</span>
            <button className="text-red-400 underline" onClick={() => onChange({ ...override, merges: override.merges.filter(x => x !== m) })}>Rozpojit</button>
          </div>
        ))}
      </div>

      {/* Dělení */}
      <div className="flex flex-col gap-1">
        <div className="text-[11px] font-semibold text-gray-500">Rozdělit (jen v této větvi)</div>
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <select className="border border-gray-200 rounded px-1 py-0.5" value={splitCode} onChange={e => setSplitCode(e.target.value)}>
            <option value="">— podskupina —</option>
            {freeSubgroups.map(sg => <option key={sg.code} value={sg.code}>{sg.label}</option>)}
          </select>
          <select className="border border-gray-200 rounded px-1 py-0.5" value={splitMethod} onChange={e => setSplitMethod(e.target.value)}>
            {SPLIT_METHOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {splitMethod === 'count' ? (
            <>
              <select className="border border-gray-200 rounded px-1 py-0.5" value={splitParts} onChange={e => setSplitParts(e.target.value)}>
                {SPLIT_PARTS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {splitParts === 'auto' && (
                <label className="flex items-center gap-1">
                  na díl:
                  <input type="number" className="border border-gray-200 rounded px-1 py-0.5 w-14" value={splitPerGroup} onChange={e => setSplitPerGroup(e.target.value)} />
                </label>
              )}
              <select className="border border-gray-200 rounded px-1 py-0.5" value={splitLang} onChange={e => setSplitLang(e.target.value)}>
                {Object.entries(SPLIT_LANG_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </>
          ) : (
            <label className="flex items-center gap-1">
              {splitMethod === 'elevation_interval' ? 'm:' : 'stanic:'}
              <input type="number" className="border border-gray-200 rounded px-1 py-0.5 w-16" value={splitParam} onChange={e => setSplitParam(e.target.value)} />
            </label>
          )}
          <button className="text-blue-600 underline disabled:opacity-40" onClick={doSplit} disabled={!splitCode}>Přidat</button>
        </div>
        {override.splits.map((s, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs bg-amber-50 rounded px-1 py-0.5">
            <span className="flex-1">
              {subgroups.find(sg => sg.code === s.memberCode)?.label ?? s.memberCode} →{' '}
              {s.method === 'count'
                ? `podle počtu (${s.parts}${s.parts === 'auto' ? `, ~${s.perGroup}/díl` : ' díly'}, ${s.lang === 'en' ? 'EN' : 'CS'})`
                : `${SPLIT_METHOD_OPTIONS.find(o => o.value === s.method)?.label} (${s.param})`}
            </span>
            <button className="text-red-400 underline" onClick={() => onChange({ ...override, splits: override.splits.filter((_, j) => j !== i) })}>Zrušit</button>
          </div>
        ))}
      </div>
    </div>
  )
}

// Kaskádový výběr parentPath — jeden select na každou nadřízenou úroveň
function ParentPathPicker({ parentBucketOptions, onPick }) {
  const [sel, setSel] = useState(parentBucketOptions.map(() => ''))
  const complete = sel.every(Boolean)
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      {parentBucketOptions.map((opts, i) => (
        <select
          key={i}
          className="border border-gray-200 rounded px-1 py-0.5"
          value={sel[i]}
          onChange={e => setSel(s => s.map((v, j) => j === i ? e.target.value : v))}
        >
          <option value="">— úroveň {i + 1} —</option>
          {opts.map(label => <option key={label} value={label}>{label}</option>)}
        </select>
      ))}
      <button
        className="text-blue-600 underline disabled:opacity-40"
        disabled={!complete}
        onClick={() => { onPick([...sel]); setSel(parentBucketOptions.map(() => '')) }}
      >Přidat úpravu</button>
    </div>
  )
}

// Jedna úroveň konfigurace — výběr typu, pak checkboxy podskupin + sloučení (jen statické def_type;
// dynamické def_type nemají checkbox seznam, viz komentář nahoře souboru)
// parentBucketOptions = pole (na každou nadřízenou úroveň) seznamu labelů jejích kbelíků — pro overrides
function LevelEditor({ level, onChange, onRemove, onMoveUp, onMoveDown, parentBucketOptions = [] }) {
  const groupTypes = useStationGroupTypes()
  const sources = useStationGroupSources(level.typeId)
  // level=1 zdroj daného typu (MVP: jedna úroveň hierarchie na level konfigurátoru, level.sourceLevel volí uživatel pokud typ má víc úrovní)
  const availableLevels = [...new Set(sources.map(s => s.level))].sort((a, b) => a - b)
  const source = sources.find(s => s.level === (level.sourceLevel ?? availableLevels[0]))
  const isDynamic = source && DYNAMIC_DEF_TYPES.has(source.def_type)

  const staticGroups = useStationGroupsBySource(!isDynamic ? source?.id : null)

  const subgroups = staticGroups.map(g => ({ code: g.code, label: g.label, station_count: g.station_count }))

  // Ulož vyřešený zdroj/def_type do level objektu, ať je viditelný i mimo tuto komponentu
  // (validace v handleApply, výpočet stromu v computeGroupTree.js).
  useEffect(() => {
    if (!source) return
    const nextDefType = isDynamic ? source.def_type : null
    if (level.dynamicSourceId === source.id && level.dynamicDefType === nextDefType) return
    const next = { ...level, dynamicSourceId: isDynamic ? source.id : null, dynamicDefType: nextDefType }
    // spatial_count: level.param nese režim dělení — nastav default 'auto', ať je úroveň platná
    // hned po zvolení typu (bez nutnosti sáhnout na select).
    if (nextDefType === 'spatial_count' && !next.param) next.param = 'auto'
    onChange(next)
  }, [source, isDynamic]) // eslint-disable-line

  const [mergeSelection, setMergeSelection] = useState([])

  // Po načtení podskupin nové úrovně zatrhni všechny naráz, ať to uživatel nemusí dělat ručně.
  // Autozatržení proběhne jen jednou na danou sadu subgroups (identifikovanou stabilním klíčem
  // z jejich id, ne referencí pole, která se mění při každém renderu) — pokud uživatel poté vše
  // ručně odebere (buckets: []), znovu se nevynutí (jinak by "Odebrat vše" nebylo možné).
  const subgroupsKey = subgroups.map(sg => sg.code).join(',')
  const autoInitKey = useRef(null)
  useEffect(() => {
    if (autoInitKey.current === subgroupsKey) return
    autoInitKey.current = subgroupsKey
    if (level.buckets.length > 0 || subgroups.length === 0) return
    const buckets = subgroups.map(sg => ({ label: sg.label, memberCodes: [sg.code] }))
    onChange({ ...level, buckets, sourceId: source.id })
  }, [subgroupsKey]) // eslint-disable-line

  const toggleBucketChecked = (subgroupCode) => {
    const inBucket = level.buckets.some(b => b.memberCodes.includes(subgroupCode))
    if (inBucket) {
      // Odebrat — smaž bucket, pokud je 1:1, jinak odeber code ze sloučeniny
      const newBuckets = level.buckets
        .map(b => b.memberCodes.includes(subgroupCode) ? { ...b, memberCodes: b.memberCodes.filter(c => c !== subgroupCode) } : b)
        .filter(b => b.memberCodes.length > 0)
      onChange({ ...level, buckets: newBuckets, sourceId: source.id })
    } else {
      const sg = subgroups.find(s => s.code === subgroupCode)
      onChange({ ...level, buckets: [...level.buckets, { label: sg.label, memberCodes: [subgroupCode] }], sourceId: source.id })
    }
  }

  const isChecked = (subgroupCode) => level.buckets.some(b => b.memberCodes.includes(subgroupCode))

  // Bucket, do kterého je daná podskupina sloučená (memberCodes.length > 1), nebo null pokud je samostatná/neisChecked
  const mergedBucketOf = (subgroupCode) => level.buckets.find(b => b.memberCodes.length > 1 && b.memberCodes.includes(subgroupCode)) ?? null

  const toggleMergeSelect = (subgroupCode) => {
    setMergeSelection(sel => sel.includes(subgroupCode) ? sel.filter(c => c !== subgroupCode) : [...sel, subgroupCode])
  }

  const mergeSelected = () => {
    if (mergeSelection.length < 2) return
    const name = window.prompt('Název sloučeniny:')
    if (!name) return
    if (level.buckets.some(b => b.label === name)) {
      window.alert('Kbelík s tímto názvem už na úrovni existuje.')
      return
    }
    // Sloučenina se vloží na místo prvního vybraného bucketu (podle pořadí v subgroups), aby zachovala pořadí (např. výškových intervalů)
    const firstIdx = level.buckets.findIndex(b => b.memberCodes.some(c => mergeSelection.includes(c)))
    const remaining = level.buckets
      .map(b => ({ ...b, memberCodes: b.memberCodes.filter(c => !mergeSelection.includes(c)) }))
      .filter(b => b.memberCodes.length > 0)
    const merged = { label: name, memberCodes: [...mergeSelection] }
    // Přepočti insert index v `remaining` — kolik bucketů před firstIdx zmizelo úplným vyprázdněním
    const removedBefore = level.buckets.slice(0, firstIdx).filter(b => b.memberCodes.every(c => mergeSelection.includes(c))).length
    const insertAt = firstIdx - removedBefore
    const newBuckets = [...remaining.slice(0, insertAt), merged, ...remaining.slice(insertAt)]
    onChange({ ...level, buckets: newBuckets })
    setMergeSelection([])
  }

  const unmerge = (bucket) => {
    const remaining = level.buckets.filter(b => b !== bucket)
    const restored = bucket.memberCodes.map(code => {
      const sg = subgroups.find(s => s.code === code)
      return { label: sg?.label ?? code, memberCodes: [code] }
    })
    const newBuckets = [...remaining, ...restored]
    // Skrytí úrovně (showAsTreeLevel=false) dávalo smysl jen pro jediný sloučený bucket;
    // po rozpojení na víc bucketů musí být úroveň zase viditelná, jinak zmizí i nově vzniklé skupiny.
    onChange({ ...level, buckets: newBuckets, showAsTreeLevel: newBuckets.length === 1 ? level.showAsTreeLevel : true })
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex flex-col shrink-0">
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 leading-none"
            onClick={onMoveUp}
            disabled={!onMoveUp}
            title="Posunout úroveň nahoru"
          >▲</button>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 leading-none"
            onClick={onMoveDown}
            disabled={!onMoveDown}
            title="Posunout úroveň dolů"
          >▼</button>
        </div>
        <select
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs flex-1"
          value={level.typeId ?? ''}
          onChange={e => onChange({ ...level, typeId: e.target.value || null, sourceLevel: null, param: null, buckets: [] })}
        >
          <option value="">— vyberte typ skupiny —</option>
          {groupTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <button className="text-xs text-red-400 underline shrink-0" onClick={onRemove}>Odebrat úroveň</button>
      </div>

      {availableLevels.length > 1 && (
        <select
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
          value={level.sourceLevel ?? availableLevels[0]}
          onChange={e => onChange({ ...level, sourceLevel: Number(e.target.value), param: null, buckets: [] })}
        >
          {availableLevels.map(lvl => <option key={lvl} value={lvl}>Úroveň {lvl}</option>)}
        </select>
      )}

      {isDynamic && source.def_type === 'spatial_count' && (
        <>
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <span className="text-gray-500">Dělení:</span>
            <select
              className="border border-gray-200 rounded-lg px-2 py-1"
              value={level.param ?? 'auto'}
              onChange={e => onChange({ ...level, param: e.target.value })}
            >
              {SPLIT_PARTS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {(level.param ?? 'auto') === 'auto' && (
              <label className="flex items-center gap-1">
                na díl:
                <input
                  type="number"
                  className="border border-gray-200 rounded-lg px-2 py-1 w-16"
                  value={level.perGroup ?? 10}
                  onChange={e => onChange({ ...level, perGroup: e.target.value })}
                />
              </label>
            )}
            <select
              className="border border-gray-200 rounded-lg px-2 py-1"
              value={level.dynamicLang ?? 'cs'}
              onChange={e => onChange({ ...level, dynamicLang: e.target.value })}
            >
              {Object.entries(SPLIT_LANG_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="text-xs text-gray-400">
            Podskupiny se dopočítají zvlášť pro každou nadřazenou skupinu až ve stromu — zde je nelze jednotlivě zatrhávat ani slučovat.
          </div>
        </>
      )}

      {isDynamic && source.def_type !== 'spatial_count' && (
        <>
          <label className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Parametr ({source.def_type === 'elevation_interval' ? 'velikost intervalu v m' : 'stanic na cluster'})</span>
            <input
              type="number"
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-24"
              value={level.param ?? ''}
              onChange={e => onChange({ ...level, param: e.target.value })}
              placeholder="např. 100"
            />
          </label>
          <div className="text-xs text-gray-400">
            Podskupiny se dopočítají zvlášť pro každou nadřazenou skupinu (např. zvlášť pro každý kraj) až ve stromu — zde je nelze jednotlivě zatrhávat ani slučovat.
          </div>
        </>
      )}

      {!isDynamic && level.typeId && (subgroups.length > 0 ? (
        <>
          <div className="flex items-center gap-1.5 text-xs px-1">
            {(() => {
              const checkedCount = subgroups.filter(sg => isChecked(sg.code)).length
              const allState = checkedCount === 0 ? 'none' : checkedCount === subgroups.length ? 'all' : 'some'
              return (
                <label className="flex items-center gap-1.5 flex-1 cursor-pointer select-none">
                  <TriStateCheckbox
                    state={allState}
                    title={allState === 'all' ? 'Odebrat vše' : 'Přidat vše'}
                    onClick={() => onChange({
                      ...level,
                      buckets: allState === 'all' ? [] : subgroups.map(sg => ({ label: sg.label, memberCodes: [sg.code] })),
                      sourceId: source.id,
                    })}
                  />
                  {allState === 'all' ? 'Odebrat vše' : allState === 'some' ? 'Přidat/odebrat vše' : 'Přidat vše'}
                </label>
              )
            })()}
            <span className="text-gray-400" title="Zaškrtněte 2+ položky pro sloučení">Sloučit</span>
            <span aria-hidden="true" className="w-3.5 shrink-0 text-center text-gray-400" title="Zaškrtněte 2+ položky pro sloučení">&#8734;</span>
          </div>
          <div className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
            {subgroups.map(sg => {
              const mergedInto = mergedBucketOf(sg.code)
              return (
                <div
                  key={sg.code}
                  className={`flex items-center gap-1.5 text-xs px-1 py-0.5 rounded ${mergedInto ? 'bg-gray-50 text-gray-400' : 'hover:bg-gray-50'}`}
                >
                  <input type="checkbox" checked={isChecked(sg.code)} onChange={() => toggleBucketChecked(sg.code)} className="accent-blue-500" />
                  <span className={`flex-1 ${mergedInto ? 'line-through' : ''}`}>{sg.label}</span>
                  <span className="text-gray-400">({sg.station_count ?? '?'})</span>
                  {mergedInto ? (
                    <span className="w-3.5 shrink-0 text-center" title={`Sloučeno do „${mergedInto.label}“`}>&#8734;</span>
                  ) : (
                    <input type="checkbox" checked={mergeSelection.includes(sg.code)} onChange={() => toggleMergeSelect(sg.code)} title="Vybrat pro sloučení" className="accent-orange-400" />
                  )}
                </div>
              )
            })}
          </div>
          {mergeSelection.length >= 2 && (
            <button className="text-xs text-blue-600 underline text-left" onClick={mergeSelected}>
              Sloučit vybrané ({mergeSelection.length})
            </button>
          )}
          {level.buckets.filter(b => b.memberCodes.length > 1).length > 0 && (
            <div className="flex flex-col gap-0.5">
              <div className="text-xs font-semibold text-gray-500">Sloučeniny</div>
              {level.buckets.filter(b => b.memberCodes.length > 1).map(b => (
                <div key={b.label} className="flex items-center gap-1.5 text-xs px-1 bg-blue-50 rounded py-0.5">
                  <span className="flex-1">
                    <span className="font-medium">{b.label}</span>
                    <span className="text-gray-500"> = {b.memberCodes.map(code => subgroups.find(s => s.code === code)?.label ?? code).join(' + ')}</span>
                  </span>
                  <button className="text-red-400 underline shrink-0" onClick={() => unmerge(b)}>Rozpojit</button>
                </div>
              ))}
            </div>
          )}
          {level.buckets.length === 1 && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={level.showAsTreeLevel !== false}
                onChange={e => onChange({ ...level, showAsTreeLevel: e.target.checked })}
                className="accent-blue-500"
              />
              Zobrazit tuto úroveň ve stromu
            </label>
          )}

          {parentBucketOptions.length > 0 && (
            <div className="flex flex-col gap-1.5 border-t border-gray-200 pt-2 mt-1">
              <div className="text-xs font-semibold text-gray-500">Úpravy podle nadřízené větve</div>
              <ParentPathPicker
                parentBucketOptions={parentBucketOptions}
                onPick={parentPath => onChange({
                  ...level,
                  overrides: [...(level.overrides ?? []), { parentPath, merges: [], splits: [] }],
                })}
              />
              {(level.overrides ?? []).map((ov, i) => (
                <OverrideEditor
                  key={ov.parentPath.join('›')}
                  override={ov}
                  subgroups={subgroups}
                  onChange={next => onChange({ ...level, overrides: level.overrides.map((o, j) => j === i ? next : o) })}
                  onRemove={() => onChange({ ...level, overrides: level.overrides.filter((_, j) => j !== i) })}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-gray-400">Žádné podskupiny.</div>
      ))}
    </div>
  )
}

const EMPTY_LEVEL = { typeId: null, sourceLevel: null, param: null, showAsTreeLevel: true, buckets: [], overrides: [], sourceId: null, dynamicSourceId: null, dynamicDefType: null }

export function GroupConfigurator({ initialGroupLevels, onApply, onCancel }) {
  const [levels, setLevels] = useState(initialGroupLevels.length > 0 ? initialGroupLevels : [{ ...EMPTY_LEVEL }])

  useEffect(() => {
    setLevels(initialGroupLevels.length > 0 ? initialGroupLevels : [{ ...EMPTY_LEVEL }])
  }, [initialGroupLevels])

  const updateLevel = (idx, next) => setLevels(ls => ls.map((l, i) => i === idx ? next : l))
  const removeLevel = (idx) => setLevels(ls => ls.filter((_, i) => i !== idx))
  const addLevel = () => setLevels(ls => [...ls, { ...EMPTY_LEVEL }])
  const moveLevel = (idx, dir) => setLevels(ls => {
    const target = idx + dir
    if (target < 0 || target >= ls.length) return ls
    const next = [...ls]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    return next
  })

  const handleApply = () => {
    // Vyfiltruj rozpracované/prázdné úrovně:
    //  - spatial_count: stačí typ (mód má default 'auto', param je volitelný)
    //  - ostatní dynamické (elevation_*/spatial_cluster): potřebují param
    //  - statické: aspoň 1 bucket
    const valid = levels.filter(l => {
      if (!l.typeId) return false
      if (l.dynamicDefType === 'spatial_count') return true
      if (l.dynamicDefType) return !!l.param
      return l.buckets.length > 0
    })
    // Vyčisti overrides: parentPath musí odkazovat na existující kbelíky nadřízených úrovní,
    // prázdné overrides (bez merges i splits) zahoď. Dynamické úrovně overrides nemají.
    const cleaned = valid.map((level, idx) => {
      if (level.dynamicDefType || !level.overrides?.length) return { ...level, overrides: [] }
      const parentLabelSets = valid.slice(0, idx).map(l => new Set(l.buckets.map(b => b.label)))
      const overrides = level.overrides.filter(ov => {
        if (ov.parentPath.length !== parentLabelSets.length) return false
        if (!ov.parentPath.every((label, i) => parentLabelSets[i]?.has(label))) return false
        return (ov.merges?.length ?? 0) > 0 || (ov.splits?.length ?? 0) > 0
      })
      return { ...level, overrides }
    })
    onApply(cleaned)
  }

  return (
    <ModalOverlay onClose={onCancel} className="z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-700">Nastavit skupinu</h2>
          <button className="text-gray-400 hover:text-gray-600" onClick={onCancel}>✕</button>
        </div>

        {levels.map((level, idx) => (
          <LevelEditor
            key={idx}
            level={level}
            onChange={next => updateLevel(idx, next)}
            onRemove={() => removeLevel(idx)}
            onMoveUp={idx > 0 ? () => moveLevel(idx, -1) : null}
            onMoveDown={idx < levels.length - 1 ? () => moveLevel(idx, 1) : null}
            parentBucketOptions={levels.slice(0, idx).map(l => l.buckets.map(b => b.label))}
          />
        ))}

        <button className="text-xs text-blue-600 underline text-left" onClick={addLevel}>
          + Přidat úroveň členění
        </button>

        <div className="flex justify-end gap-2 mt-2">
          <button className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50" onClick={onCancel}>Zrušit</button>
          <button className="px-3 py-1.5 text-xs rounded-lg bg-blue-500 text-white hover:bg-blue-600" onClick={handleApply}>Použít</button>
        </div>
      </div>
    </ModalOverlay>
  )
}
